import React, { createContext, useContext, useState } from "react";
import PropTypes from "prop-types";
import useLogger from "../util/Logger";
import WorkOrderModel from "../models/WorkOrderModel";
import WorkItemModel from "../models/WorkItemModel";
import WorkOrderEntryModel from "../models/WorkOrderEntryModel";
import CreateWorkOrderModel from "./webServiceModels/CreateWorkOrderModel";
import WorkOrderEntryModelBuilder from "../models/WorkOrderEntryModelBuilder";
import UpdateWorkOrderModel from "./webServiceModels/UpdateWorkOrderModel";
import getCreateWorkItemModelByTemplate from "../util/getCreateWorkItemModelByTemplate";
import { useAuth } from "./AuthHook";

const WorkOrdersContext = createContext();

export default function WorkOrdersProvider({ children }) {
  const logger = useLogger();
  const auth = useAuth();
  const [workEntry, setWorkEntry] = useState(
    new WorkOrderEntryModel(null, null, null, null, null)
  );

  const workOrderSorter = (a, b) => {
    const aDate = Date.parse(a.createdon);
    const bDate = Date.parse(b.createdon);
    if (aDate > bDate) return 1;
    if (aDate < bDate) return -1;
    return 0;
  };

  const workItemSorter = (a, b) => {
    const aDate = Date.parse(a.createdOn);
    const bDate = Date.parse(b.createdOn);
    if (aDate > bDate) return 1;
    if (aDate < bDate) return -1;
    return 0;
  };

  const getWorkOrders = async (propertyId) => {
    logger.debug(
      "WorkOrdersProvider.getWorkOrders",
      `GET field/workOrder/property/${propertyId}?pageNumber=1&pageSize=1000`
    );
    const data = await auth.get(
      `field/workOrder/property/${propertyId}?pageNumber=1&pageSize=1000`
    );

    const workOrders = [];
    logger.debug("WorkOrdersProvider.getWorkOrders", `Sorting work orders`);
    const sortedWorkOrders = data.sort((a, b) => workOrderSorter(a, b));

    logger.debug(
      "WorkOrdersProvider.getWorkOrders",
      `Beginning processing work orders`
    );

    sortedWorkOrders.forEach((current) => {
      logger.debug(
        "WorkOrdersProvider.getWorkOrders",
        `Processing work order '${current.WorkOrderID}'`
      );

      if (current.WorkOrder) {
        const model = new WorkOrderModel();
        model.id = current.WorkOrderID;
        model.propertyId = current.WorkOrder.property.id;
        model.propertyNumber = current.WorkOrder.property.title;
        model.propertyName = current.WorkOrder.property.subtitle;
        model.reference = current.WorkOrder.reference;
        model.createdOn = current.WorkOrder.createdon
          ? Date.parse(current.WorkOrder.createdon)
          : null;
        model.closedOn = current.WorkOrder.closedon
          ? Date.parse(current.WorkOrder.closedon)
          : null;
        model.type = current.WorkOrder.type;
        model.category = current.WorkOrder.workordercategory?.title;
        model.subCategory = current.WorkOrder.workordersubcategory?.title;
        model.state = current.state;
        model.status = current.WorkOrder.status;
        model.description = current.WorkOrder.description;
        model.diagnosis = current.WorkOrder.diagnosis;
        model.reporterName = current.WorkOrder.reportername;
        model.reporterType = current.WorkOrder.reportertype;
        model.reporterTelephone = current.WorkOrder.reporterphonenumber;
        model.reporterEmail = current.WorkOrder.reporteremailaddress;
        model.customerName = current.WorkOrder.customerresidentname;
        model.customerEmail = current.WorkOrder.customeremailaddress;
        model.customerTelephone = current.WorkOrder.customercontactnumber;
        model.cmvDueDate = current.WorkOrder.duedate;
        model.cmvContactDate = current.WorkOrder.contactdate;
        model.redFlag = current.WorkOrder.redFlag;
        model.projectId = current.WorkOrder?.project?.id;
        model.projectName = current.WorkOrder?.project?.subtitle;
        model.workItems = current.WorkItems.sort((a, b) =>
          workItemSorter(a, b)
        ).map(
          (currentWorkItem) =>
            new WorkItemModel(currentWorkItem, current.WorkOrder)
        );

        workOrders.push(model);
      }
    });

    logger.debug(
      "WorkOrdersProvider.getWorkOrders",
      `Finished processing work orders`
    );

    return workOrders;
  };

  const getWorkOrderForUpdate = async (
    propertyId,
    workOrderId,
    workItemCategories
  ) => {
    logger.debug(
      "WorkOrdersProvider.getWorkOrderForUpdate",
      `GET field/workOrder/property/${propertyId}?pageNumber=1&pageSize=1000`
    );
    const data = await auth.get(
      `field/workOrder/property/${propertyId}?pageNumber=1&pageSize=1000`
    );

    // Find the correct WO
    const found = data.find((element) => element.WorkOrderID === workOrderId);
    if (!found)
      logger.error(
        "WorkOrdersProvider.getWorkOrderForUpdate",
        `Work order ${workOrderId} not found.`
      );
    if (!found) throw new Error(`Work order ${workOrderId} not found.`);

    const model = WorkOrderEntryModelBuilder(found, workItemCategories);

    logger.debug(
      "WorkOrdersProvider.getWorkOrders",
      `Finished processing work orders`
    );
    return model;
  };

  const clearEntry = (
    propertyId,
    siteId,
    siteName,
    territoryId,
    territoryName,
    customerId,
    customerName,
    redFlag,
    propertyAccessNotes,
    workitemtemplateid,
    workitemtemplateref,
    preferredAppointmentSlot,
    preferredAppointmentSlotTitle,
    appointmentConfirmed,
    appointmentConfirmedTitle,
    workitemcategoryid,
    workitemcategoryname,
    type,
    asset,
    category,
    subCategory,
    categoryName,
    subCategoryName,
    reporterType,
    poNumber,
    slaHours
  ) => {
    setWorkEntry(
      new WorkOrderEntryModel(
        propertyId,
        siteId,
        siteName,
        territoryId,
        territoryName,
        customerId,
        customerName,
        redFlag,
        propertyAccessNotes,
        workitemtemplateid,
        workitemtemplateref,
        preferredAppointmentSlot,
        preferredAppointmentSlotTitle,
        appointmentConfirmed,
        appointmentConfirmedTitle,
        workitemcategoryid,
        workitemcategoryname,
        type,
        asset,
        category,
        subCategory,
        categoryName,
        subCategoryName,
        reporterType,
        poNumber,
        slaHours
      )
    );
  };

  const getEntry = () => workEntry;

  const setEntry = (workOrder) => {
    setWorkEntry({ 
      ...workOrder, 
      createdBy: `${auth.user.idToken.payload.given_name} ${auth.user.idToken.payload.family_name}` 
    });
  };

  // workOrder as defined in WorkOrderEntryModel
  const createNewWorkOrder = async (workOrder) => {
    logger.debug(
      "WorkOrdersProvider.createNewWorkOrder",
      `PUT field/workOrder`
    );
    const webServiceWorkOrder = CreateWorkOrderModel(workOrder);
    const data = await auth.put(`field/workOrder`, webServiceWorkOrder);
    return {
      reference: data.reference,
      propertyName: data.property.Subtitle || data.property.subtitle,
      propertyNumber: data.property.Title || data.property.title,
    };
  };

  // workOrder as defined in WorkOrderEntryModel
  const updateWorkOrder = async (workOrder) => {
    logger.debug("WorkOrdersProvider.updateWorkOrder", `PATCH field/workOrder`);
    const webServiceWorkOrder = UpdateWorkOrderModel(workOrder);
    await auth.patch(`field/workOrder`, webServiceWorkOrder);
  };

  const cancelWorkOrder = async (workOrderReference) => {
    logger.debug("WorkOrdersProvider.cancelWorkOrder", `PATCH field/workOrder`);
    const webServiceWorkOrder = {
      reference: workOrderReference,
      status: "Cancelled",
    };
    await auth.patch(`field/workOrder`, webServiceWorkOrder);
  };
  const closeWorkOrder = async (workOrderReference) => {
    logger.debug("WorkOrdersProvider.closeWorkOrder", `PATCH field/workOrder`);
    const webServiceWorkOrder = {
      reference: workOrderReference,
      status: "Closed",
    };
    await auth.patch(`field/workOrder`, webServiceWorkOrder);
  };

  const createNewWorkOrderItem = async (workOrder) => {
    // This route is creating Work Item AND Work Order
    logger.debug(
      "WorkOrdersProvider.createNewWorkOrderItem",
      `PUT field/workItem`
    );
    const createWorkItemModel = getCreateWorkItemModelByTemplate(
      workOrder.workitemtemplateref
    );
    const webServiceWorkOrderItem = createWorkItemModel(workOrder);
    await auth.put(`field/workItem`, webServiceWorkOrderItem);
  };

  return (
    <WorkOrdersContext.Provider
      value={{
        getWorkOrders,
        clearEntry,
        getEntry,
        setEntry,
        createNewWorkOrder,
        createNewWorkOrderItem,
        getWorkOrderForUpdate,
        updateWorkOrder,
        cancelWorkOrder,
        closeWorkOrder,
      }}
    >
      {children}
    </WorkOrdersContext.Provider>
  );
}

WorkOrdersProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useWorkOrders = () => useContext(WorkOrdersContext);
